import React, { useEffect, useRef } from "react";
import { Box, Grid, IconButton, InputAdornment, Popover, TextField, Typography } from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CloseIcon from "@mui/icons-material/Close";

import globalStyles from "../../../../assets/styles/global.module.scss";
import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";
import formStyles from "../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import SampleVariableKeyLabel from "./SampleVariableKeyLabel";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { isTemplateCategoryAuthentication } from "../../channels/global/whatsapp/templates/utils";
import { isTemplateCreated } from "../constant";

export type VariableField = {
    whatsapp_template_dynamic_variable_key: string;
    whatsapp_template_dynamic_variable_sample_value?: string;
    [key: string]: any;
};

export type VariableDialogProps = {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    title: string;
    variableData: VariableField[];
    variableType: "header" | "body";
    activeVariableKey?: string | null;
    onChange: (key: string, event: any, type: string) => void;
    onRemoveField: (key: string, type: string) => void;
    hasMissingSampleText: boolean;
    disabled?: boolean;
    hideDeleteButton?: boolean;
    requiredVariableKeys?: string[];
    warningText?: string;
};

function VariableDialog({
    open,
    anchorEl,
    onClose,
    title,
    variableData,
    variableType,
    activeVariableKey,
    onChange,
    onRemoveField,
    hasMissingSampleText,
    disabled = false,
    hideDeleteButton = false,
    requiredVariableKeys = [],
    warningText,
}: VariableDialogProps) {
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const firstInputRef = useRef<HTMLInputElement | null>(null);

    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);

    const isAuthenticationTemplate = isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);
    const shouldShowDeleteButton = !hideDeleteButton && !isTemplateCreated(createTemplatePayload) && !isAuthenticationTemplate;
    const requiredVariableKeySet = new Set(
        requiredVariableKeys.map((key) => key?.toLowerCase())
    );
    const dialogWarningText = warningText || (hasMissingSampleText ? "Add sample text" : "");
    
    const handleRemoveClick = (key: string) => {
        onRemoveField(key, variableType);
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        if (disabled) return;

        const t = window.setTimeout(() => {
            const key = activeVariableKey ?? undefined;
            const el = (key ? inputRefs.current[key] : null) || firstInputRef.current;
            if (!el) return;
            el?.focus();
            const len = el.value?.length ?? 0;
            el?.setSelectionRange?.(len, len);
        }, 0);

        return () => window.clearTimeout(t);
    }, [open, disabled, activeVariableKey, variableData?.length]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ className: styles.variableDialog }}
        >
            <Grid container display="flex" justifyContent="space-between" alignItems="center" className={styles.variableDialog__TitleContainer}>
                <Typography className={styles.variableDialog__Title}>
                    {title}
                </Typography>
                <CloseIcon onClick={onClose} className={`${globalStyles.clickable} ${styles.variableDialog__CloseIcon}`} />
            </Grid>

            <Typography className={styles.variableDialog__SubTitleText}>
                Sample Text
            </Typography>

            <Box className={styles.variableDialog__Content}>
                {variableData?.map((field: VariableField, ind: number) => {
                    
                    const isFieldDisabled = disabled || !!(isTemplateCreated(createTemplatePayload) && isAuthenticationTemplate);
                    const sampleValue = field?.whatsapp_template_dynamic_variable_sample_value ?? "";
                    const showClearSampleIcon = sampleValue.trim() !== "" && !isFieldDisabled;
                    const variableKey = field?.whatsapp_template_dynamic_variable_key ?? "";
                    const isRequiredField = requiredVariableKeySet?.has(variableKey.toLowerCase());

                    return (
                    <Grid key={ind} container className={styles.variableDialog__Row} flexWrap="nowrap">
                        
                        <Grid item className={styles.variableDialog__LabelColumn}>
                            <SampleVariableKeyLabel
                                variableKey={variableKey}
                                className={styles.variableDialog__Label}
                                required={isRequiredField}
                            />
                        </Grid>

                        <Grid item xs sx={{ minWidth: 0, flex: 1 }}>
                            <TextField
                                disabled={isFieldDisabled}
                                size="small"
                                variant="outlined"
                                fullWidth
                                autoComplete="off"
                                className={styles.variableDialog__Input}
                                inputRef={(el) => {
                                    inputRefs.current[field?.whatsapp_template_dynamic_variable_key] = el;
                                    if (ind === 0) firstInputRef.current = el;
                                }}
                                onChange={(e) => onChange(field?.whatsapp_template_dynamic_variable_key, e, variableType)}
                                onKeyDown={(e) => {
                                    if (disabled) return;
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        onClose();
                                    }
                                }}
                                value={sampleValue}
                                InputProps={{
                                    endAdornment: showClearSampleIcon ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                edge="end"
                                                aria-label="Clear sample text"
                                                onClick={() => onChange(field?.whatsapp_template_dynamic_variable_key, { target: { value: "" } }, variableType)}
                                                className={globalStyles.clickable}
                                            >
                                                <CloseIcon className={formStyles.sampleVariableClearIcon} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Grid>

                        {shouldShowDeleteButton && (
                            <Grid item>
                                <IconButton
                                    onClick={() => handleRemoveClick(field?.whatsapp_template_dynamic_variable_key)}
                                    size="small"
                                    className={styles.variableDialog__DeleteIconBtn}
                                    disabled={(isTemplateCreated(createTemplatePayload) || isAuthenticationTemplate) ? true : false}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Grid>
                        )}
                        
                    </Grid>
                    );
                })}
            </Box>

            {!!dialogWarningText && (
                <Box className={styles.variableDialog__Warning}>
                    <ReportProblemOutlinedIcon className={styles.variableDialog__WarningIcon} />
                    <span className={styles.variableDialog__WarningText}>
                        {dialogWarningText}
                    </span>
                </Box>
            )}

        </Popover>
    );
}

export default VariableDialog;
